// ─── Styles ─────────────────────────────────────────────────────────────────
import "../styles/templates.scss";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
// ─── Design-system / shared components ───────────────────────────────────────
import PageLayout from "../components/page-layout";
import { GuiButton } from "../components/global-ui/gui-button";
import { GuiSelect } from "../components/global-ui/gui-select";
import { GuiTextbox } from "../components/global-ui/gui-textbox";
import { GuiTextArea } from "../components/global-ui/gui-textarea";
import GuiHtmlEditor from "../components/global-ui/gui-html-editor";
import AudioPlayer from "../components/audio-player";
import * as DOMPurify from "dompurify";
// ─── Utilities, constants & navigation ───────────────────────────────────────
import {
  AlertType,
  Path,
  COMM_METHODS,
  TEMPLATE_ID_MAX,
  getTemplateCharLimit,
  generateTemplateId,
  getAlertTypeConfig,
  getCommMethodFromConfig,
  generateVoiceFileName,
  MAX_TTS_DURATION_SECONDS,
  TTS_CHAR_WARNING_LIMIT,
  TTS_TRIM_TARGET_SECONDS,
} from "../utilities/constants";
import {
  onlyDigits,
  useUpdateState,
  getAudioDurationSeconds,
  estimateTtsDurationSecondsCalibrated,
  formatDurationShort,
  trimTtsTextToDuration,
} from "../utilities/utilities";
import audioRecorder from "../utilities/audio-recording";
import navigate from "../utilities/navigation";
import { showToast } from "../components/toast";
import api from "../api/api";
import { ManageTemplateCategoriesModal } from "../components/manage-template-categories-modal";

const ALLOWED_VOICE_EXTENSIONS = [".wav", ".mp3"];
const ALLOWED_VOICE_ACCEPT = ".wav,.mp3";

/**
 * TemplateCreate — Create or edit a message template.
 *
 * Supports Text, Email, Voice, and TTS communication methods.
 * In edit mode the template is loaded by ID from route params;
 * in create mode a new template ID is auto-generated.
 *
 * Layout: two-column — left (details + message), right (live preview + actions).
 * Mobile users are redirected to the Dashboard (templates are desktop-only).
 */
export default function TemplateCreate(props) {
  // ── Redux selectors ──────────────────────────────────────────────────────
  const token = useSelector((store) => store.user.token);
  const isMobile = useSelector((store) => store.page.isMobile);

  // ── Route params — create vs. edit mode ─────────────────────────────────
  const id = props.match && props.match.params && props.match.params.id;
  const isEdit = !!id;

  // ── Hidden file input ref for voice upload ───────────────────────────────
  const voiceFileInputRef = useRef(null);
  const emailHtmlEditorRef = useRef(null);
  const emailEditorHydratedRef = useRef(false);
  // ── Guard: auto-TTS on edit load triggers only once ──────────────────────
  const autoTTSTriggeredRef = useRef(false);
  // ── Recording timer ──────────────────────────────────────────────────────
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingTimerRef = useRef(null);
  const recordingPollTimerRef = useRef(null);
  // ── Redirect mobile users ────────────────────────────────────────────────
  useEffect(() => {
    if (isMobile) navigate(Path.Dashboard);
  }, [isMobile]);

  // ── Local state ──────────────────────────────────────────────────────────
  const [state, updateState] = useUpdateState({
    // Core template fields
    commMethod: AlertType.Text,
    templateName: "",
    subject: "",
    message: "",
    categoryId: null,
    templateId: isEdit ? "" : generateTemplateId(),

    // Voice-only: preview call fields (UI-only, not persisted)
    previewPhone: "",
    previewExtension: "",
    ttsPreviewPhone: "", // TTS preview call phone number (UI-only, not persisted)
    ttsPreviewExtension: "", // TTS preview call extension (UI-only, not persisted)
    recordingPhone: "",
    recordingSessionId: null,
    recordingCallInProgress: false,
    recordingStatusPolling: false,
    recordingProcessed: false,
    recordingStatusMessage: "",
    canRetryRecordingCall: false,

    // Voice file attachment
    voiceFileUrl: null, // Azure Blob URL → saved to DB + used in AudioPlayer
    voiceFileIdentifier: null, // CDYNE soundFileId → preview calls only
    voiceFileName: null, // original filename for display
    voiceLocalPreviewUrl: null, // local blob:// URL for AudioPlayer preview (upload mode)
    voiceUploading: false,
    voiceFileError: "",

    // Recorder widget state
    voiceMode: "upload", // "upload" | "record"
    voiceRecordingPreviewUrl: null, // object URL for local playback after recording

    // Dropdown options
    categoryOptions: [],

    // UI flags
    saving: false,
    loading: false,
    detailsOpen: true,
    submitted: false,
    showManageCategories: false,

    // TTS conversion
    ttsAudioUrl: null,
    convertingTTS: false,
    ttsConverted: false, // true once converted; resets when message body changes
  });

  // ── Initial data fetch ───────────────────────────────────────────────────
  useEffect(() => {
    loadCategoryOptions();
    if (isEdit) loadTemplate();
  }, []);

  useEffect(() => {
    if (!isEdit || !isEmail || state.loading || !state.message) return;
    if (emailEditorHydratedRef.current) return;

    emailEditorHydratedRef.current = true;
    emailHtmlEditorRef.current?.setHtml?.(state.message);
  }, [isEdit, state.loading, state.message, state.commMethod]);

  // ── Recording timer cleanup on unmount ───────────────────────────────────
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (recordingPollTimerRef.current)
        clearTimeout(recordingPollTimerRef.current);
    };
  }, []);

  // ── Preview panel drag-to-resize ─────────────────────────────────────────
  const rightPanelRef = useRef(null);

  const onDragHandleMouseDown = useCallback((e) => {
    e.preventDefault();
    const panel = rightPanelRef.current;
    if (!panel) return;

    const startX = e.clientX;
    const startWidth = panel.getBoundingClientRect().width;

    const onMouseMove = (moveEvent) => {
      // Dragging left (negative delta) increases width
      const delta = startX - moveEvent.clientX;
      const newWidth = Math.min(
        Math.max(startWidth + delta, 300),
        window.innerWidth * 0.7,
      );
      panel.style.width = newWidth + "px";
      panel.style.flex = "0 0 auto";
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Data helpers
  // ─────────────────────────────────────────────────────────────────────────

  async function loadCategoryOptions() {
    const result = await api.templateCategories.getAll(token);
    if (result?.success) {
      updateState({
        categoryOptions: (result.categories || []).map((c) => ({
          value: c.id,
          label: c.name,
        })),
      });
    }
  }

  async function loadTemplate() {
    updateState({ loading: true });
    const result = await api.alerts.getTemplateById(token, id);
    if (result?.success) {
      const commMethod = getCommMethodFromConfig(
        result.alertTypeId ?? result?.AlertTypeId,
        result.templateType ?? result?.TemplateType,
      );
      const rawTemplate =
        result.template ?? result?.Template ?? result?.message ?? "";

      const voiceFileName =
        result.voiceFileName ?? result?.VoiceFileName ?? null;

      const isBlobStorageUrl =
        commMethod === AlertType.Voice &&
        rawTemplate.startsWith("https://") &&
        rawTemplate.includes("blob.core.windows.net");

      const soundFileId =
        commMethod === AlertType.Voice ? extractSoundFileId(rawTemplate) : null;

      // voiceAudioUrl comes from GetTemplateById — either a direct blob URL or
      // a data: URL with embedded audio bytes. Both work as <audio src> directly.
      const voiceAudioUrl =
        result.voiceAudioUrl ?? result?.VoiceAudioUrl ?? null;

      updateState({
        commMethod,
        templateName: result.subject ?? result?.Subject ?? "",
        subject: result.subject ?? result?.Subject ?? "",
        message: commMethod === AlertType.Voice ? "" : rawTemplate,
        voiceFileUrl:
          commMethod === AlertType.Voice
            ? isBlobStorageUrl
              ? rawTemplate
              : soundFileId
            : null,
        voiceFileIdentifier: soundFileId,
        voiceFileName:
          commMethod === AlertType.Voice
            ? voiceFileName ||
              (soundFileId ? soundFileId.split("-").slice(1).join("-") : null)
            : null,
        // voiceAudioUrl is directly usable as <audio src> — no extra fetch needed
        voiceLocalPreviewUrl:
          commMethod === AlertType.Voice ? voiceAudioUrl : null,
        // Always clear recording preview URL so stale data from a previous
        // recording session doesn't bleed into edit mode.
        voiceRecordingPreviewUrl: null,
        voiceMode: "upload",
        categoryId:
          result.templateCategoryId ?? result?.TemplateCategoryId ?? null,
        templateId: result.templateId ?? result?.TemplateId ?? "",
        loading: false,
      });
    } else {
      updateState({ loading: false });
    }
  }

  /** Extracts the bare CDYNE soundFileId from any stored template body format.
   *  Handles both legacy blobsync URLs and new bare-ID saves.
   *  e.g. "https://dev.mycast.site/ws/blobsync.aspx?vFileId=keep_27-Name"
   *       → "keep_27-Name"
   *  e.g. "keep_27-Name" → "keep_27-Name"
   */
  function extractSoundFileId(template) {
    if (!template) return template;
    try {
      const url = new URL(template);
      const id = url.searchParams.get("vFileId");
      if (id) return id;
    } catch (_) {}
    return template;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Voice — file upload
  // ─────────────────────────────────────────────────────────────────────────

  function handleVoiceFileClick() {
    if (voiceFileInputRef.current) {
      voiceFileInputRef.current.value = "";
      voiceFileInputRef.current.click();
    }
  }

  async function handleVoiceFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ALLOWED_VOICE_EXTENSIONS.includes(ext)) {
      // Revoke old local preview URL if any
      if (state.voiceLocalPreviewUrl)
        URL.revokeObjectURL(state.voiceLocalPreviewUrl);
      updateState({
        voiceFileError: "Only .wav and .mp3 files are allowed.",
        voiceFileUrl: null,
        voiceFileIdentifier: null,
        voiceFileName: null,
        voiceLocalPreviewUrl: null,
      });
      return;
    }

    // Create an immediate local preview URL so the AudioPlayer has something to play
    if (state.voiceLocalPreviewUrl)
      URL.revokeObjectURL(state.voiceLocalPreviewUrl);
    const localPreviewUrl = URL.createObjectURL(file);

    updateState({
      voiceUploading: true,
      voiceFileError: "",
      voiceLocalPreviewUrl: localPreviewUrl,
    });
    const result = await api.file.uploadVoiceFile(token, file);
    updateState({ voiceUploading: false });

    if (result?.success) {
      updateState({
        voiceFileUrl: result.url,
        voiceFileName: result.fileName || file.name,
        voiceFileError: "",
      });
    } else {
      URL.revokeObjectURL(localPreviewUrl);
      updateState({
        voiceFileError:
          result?.errorMessage || "Upload failed. Please try again.",
        voiceLocalPreviewUrl: null,
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Voice — recorder
  // ─────────────────────────────────────────────────────────────────────────

  async function handleRecordingComplete(audioBlob) {
    // Create a local object URL for immediate playback preview
    const previewUrl = URL.createObjectURL(audioBlob);
    const recordedAt = new Date();

    updateState({
      voiceUploading: true,
      voiceFileError: "",
      voiceRecordingPreviewUrl: previewUrl,
      recordedAt,
    });

    // Upload to the template-specific recording endpoint: POST /api/file/recording-template
    const result = await api.file.uploadTemplateRecording(
      token,
      audioBlob,
      audioBlob.type,
    );
    updateState({ voiceUploading: false });

    if (result?.success && result?.url) {
      updateState({
        voiceFileUrl: result.url,
        voiceFileError: "",
        recordingProcessed: true,
        canRetryRecordingCall: false,
      });
    } else {
      updateState({
        voiceFileError:
          result?.errorMessage || "Recording upload failed. Please try again.",
        voiceRecordingPreviewUrl: null,
        recordedAt: null,
      });
    }
  }

  async function startRecording() {
    try {
      await audioRecorder.start();
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
      updateState({ isRecording: true, voiceFileError: "" });
    } catch (err) {
      const msg =
        err?.message || err?.name || "Microphone access denied or unavailable.";
      showToast("error", msg);
    }
  }

  async function stopRecording() {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    try {
      const audioBlob = await audioRecorder.stop();
      updateState({ isRecording: false });
      handleRecordingComplete(audioBlob);
    } catch (err) {
      updateState({ isRecording: false });
      const msg =
        err?.message || err?.name || "Recording failed. Please try again.";
      showToast("error", msg);
    }
  }

  async function handleStartPhoneRecording() {
    const voiceFileName = (state.voiceFileName || "").trim();
    const digits = onlyDigits(state.recordingPhone || "");

    if (!voiceFileName) {
      updateState({ voiceFileError: "Voice File Name is required" });
      return;
    }

    if (digits.length !== 10) {
      updateState({ voiceFileError: "Phone number must be 10 digits." });
      return;
    }

    updateState({
      voiceFileError: "",
      recordingCallInProgress: true,
      canRetryRecordingCall: false,
      recordingProcessed: false,
      recordingStatusMessage:
        "The system will call you. Please follow the instructions on your phone.",
    });

    const result = await api.file.startTemplateRecordingCall(token, {
      voiceFileName,
      phoneNumber: digits,
    });

    if (!result?.success || !result?.sessionId) {
      updateState({
        recordingCallInProgress: false,
        canRetryRecordingCall: true,
        voiceFileError:
          result?.errorMessage || "Unable to place call. Please retry.",
      });
      return;
    }

    updateState({
      recordingSessionId: result.sessionId,
      recordingCallInProgress: false,
      recordingStatusPolling: true,
      recordingStatusMessage: "Please wait as we process your file...",
    });
    pollRecordingStatus(result.sessionId, 0);
  }

  async function pollRecordingStatus(sessionId, attempt) {
    if (!sessionId || attempt >= 60) {
      updateState({
        recordingStatusPolling: false,
        canRetryRecordingCall: true,
        voiceFileError:
          "We did not receive your recording yet. Click retry if you did not get a phone call.",
      });
      return;
    }

    const result = await api.file.getTemplateRecordingStatus(token, sessionId);
    if (!result?.success) {
      updateState({
        recordingStatusPolling: false,
        canRetryRecordingCall: true,
        voiceFileError: result?.errorMessage || "Unable to process recording.",
      });
      return;
    }

    if (result.status === "completed" && result.voiceFileIdentifier) {
      updateState({
        recordingStatusPolling: false,
        recordingStatusMessage: "",
        recordingProcessed: true,
        canRetryRecordingCall: false,
        voiceFileError: "",
        // voiceFileUrl = full public blob URL (stored in template column and used for playback).
        // Falls back to CDYNE ID if blob upload failed on the server.
        voiceFileUrl: result.voiceFileUrl || result.voiceFileIdentifier,
        voiceFileIdentifier: result.voiceFileIdentifier, // CDYNE ID — preview calls only
        voiceRecordingPreviewUrl: result.voiceFileUrl || null, // blob URL — directly playable
      });
      return;
    }

    if (result.status === "failed") {
      updateState({
        recordingStatusPolling: false,
        canRetryRecordingCall: true,
        voiceFileError:
          result?.errorMessage || "Recording failed. Please retry.",
      });
      return;
    }

    recordingPollTimerRef.current = setTimeout(
      () => pollRecordingStatus(sessionId, attempt + 1),
      5000,
    );
  }

  async function handleSendPreviewCall() {
    const digits = onlyDigits(state.previewPhone || "");
    if (digits.length !== 10) {
      showToast("error", "Preview phone number must be 10 digits.");
      return;
    }

    if (!state.voiceFileUrl && !state.voiceFileIdentifier) {
      showToast("error", "A recorded voice file is required.");
      return;
    }

    const result = await api.file.sendTemplatePreviewCall(token, {
      phoneNumber: digits,
      voiceFileUrl: state.voiceFileUrl || "",
      voiceFileIdentifier: state.voiceFileIdentifier,
    });

    if (result?.success) {
      showToast("success", "Preview Sent");
    }
  }

  async function handleSendTtsPreviewCall() {
    const digits = onlyDigits(state.ttsPreviewPhone || "");
    if (digits.length !== 10) {
      showToast("error", "Preview phone number must be 10 digits.");
      return;
    }

    if (!state.ttsAudioUrl) {
      showToast(
        "error",
        "Convert your message to speech before placing a preview call.",
      );
      return;
    }

    const result = await api.file.sendTemplateTtsPreviewCall(token, {
      phoneNumber: digits,
      ttsMessage: state.message,
    });

    if (result?.success) {
      showToast("success", "Preview sent.");
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TTS conversion
  // ─────────────────────────────────────────────────────────────────────────

  async function handleConvertToSpeech(textOverride) {
    // textOverride is only ever a string when called from a trim action; button
    // onClick handlers pass a DOM event, so guard against treating that as text.
    const overrideText = typeof textOverride === "string" ? textOverride : null;
    const textToConvert = overrideText || state.message;
    if (
      !textToConvert?.trim() ||
      state.convertingTTS ||
      (!overrideText && state.ttsConverted)
    )
      return;
    updateState({
      convertingTTS: true,
      ttsAudioUrl: null,
      ttsDurationSeconds: null,
    });
    const result = await api.file.textToSpeech(token, textToConvert);
    const durationSeconds = result?.success
      ? await getAudioDurationSeconds(result?.fileUrl)
      : null;
    // Calibrate the estimator to the rate actually observed for this conversion,
    // so the live counter stops drifting from a generic per-language guess.
    const charsPerSecond =
      durationSeconds > 0 ? textToConvert.length / durationSeconds : null;
    updateState({
      convertingTTS: false,
      ttsDurationSeconds: durationSeconds,
      ttsCharsPerSecond: charsPerSecond,
      ttsBannerDismissed: false,
    });
    if (result?.success) {
      updateState({ ttsAudioUrl: result.fileUrl, ttsConverted: true });
    } else {
      showToast(
        "error",
        result?.errorMessage ||
          "Failed to convert to speech. Please try again.",
      );
    }
  }

  // Never truncates or rate-adjusts audio — only re-converts a shorter, sentence-aligned
  // portion of the source text. The dropped remainder is kept (not deleted) for display.
  function applyTtsTrim(targetSeconds) {
    const { keptText, overflowText, trimmed } = trimTtsTextToDuration(
      state.message,
      state.ttsDurationSeconds,
      targetSeconds,
    );
    if (!trimmed || !keptText) return;
    updateState({ ttsTrim: { keptText, overflowText } });
    handleConvertToSpeech(keptText);
  }

  function handleShortenToFit() {
    // Small safety margin so the re-converted audio reliably lands under the hard limit.
    applyTtsTrim(MAX_TTS_DURATION_SECONDS - 5);
  }

  function handleTrimTo105() {
    applyTtsTrim(TTS_TRIM_TARGET_SECONDS);
  }

  function dismissTtsDurationBanner() {
    updateState({ ttsBannerDismissed: true });
  }

  // Auto-convert TTS in edit mode once the template has finished loading
  useEffect(() => {
    if (
      isEdit &&
      !autoTTSTriggeredRef.current &&
      state.commMethod === AlertType.VoiceIVR &&
      state.message.trim() &&
      !state.loading &&
      !state.ttsConverted &&
      !state.convertingTTS
    ) {
      autoTTSTriggeredRef.current = true;
      handleConvertToSpeech();
    }
  }, [state.message, state.loading, state.commMethod, state.ttsConverted]);

  // ─────────────────────────────────────────────────────────────────────────
  // Save / update
  // ─────────────────────────────────────────────────────────────────────────

  async function save() {
    updateState({ submitted: true });
    if (!canSave) return;

    updateState({ saving: true });
    const { AlertTypeId, TemplateType } = getAlertTypeConfig(state.commMethod);
    const templateBody = isVoice ? state.voiceFileUrl || "" : state.message;
    const voiceFileName = isVoice ? state.voiceFileName || null : null;

    const payload = {
      AlertTypeId,
      TemplateType,
      Subject: state.subject,
      Template: templateBody,
      TemplateId: state.templateId || null,
      TemplateCategoryId: state.categoryId || null,
      VoiceFileName: voiceFileName,
    };

    const result = isEdit
      ? await api.alerts.editTemplate(token, { Id: Number(id), ...payload })
      : await api.alerts.saveTemplate(token, payload);

    updateState({ saving: false });

    if (result?.success) {
      showToast(
        "success",
        isEdit
          ? "Template updated successfully."
          : "Template saved successfully.",
      );
      navigate(Path.Templates);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Derived values
  // ─────────────────────────────────────────────────────────────────────────

  const isEmail = state.commMethod === AlertType.Email;
  const isVoice = state.commMethod === AlertType.Voice;
  const isTTS = state.commMethod === AlertType.VoiceIVR;

  const limit = getTemplateCharLimit(state.commMethod);
  const charCount = state.message ? state.message.length : 0;
  const atLimit = limit && charCount >= limit;

  const showErrors = state.submitted;
  const templateIdError =
    state.templateId.length > TEMPLATE_ID_MAX
      ? `Template ID must be ${TEMPLATE_ID_MAX} characters or less`
      : "";
  const subjectError =
    showErrors && !state.subject.trim() ? "Subject is required" : "";
  const messageError =
    showErrors && !state.message.trim() ? "Message is required" : "";
  const voiceFileRequiredError =
    showErrors && isVoice && !state.voiceFileUrl && !state.voiceUploading
      ? "A voice file is required"
      : "";
  const voiceFileNameError =
    showErrors &&
    isVoice &&
    state.voiceMode === "record" &&
    !state.voiceFileName?.trim()
      ? "Voice File Name is required"
      : "";

  const canSave =
    !!state.subject.trim() &&
    !templateIdError &&
    !state.voiceUploading &&
    !state.recordingCallInProgress &&
    !state.recordingStatusPolling &&
    !(isTTS && state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS) &&
    (isVoice
      ? !!state.voiceFileUrl &&
        (state.voiceMode !== "record" || !!state.voiceFileName?.trim()) &&
        (state.voiceMode !== "record" ||
          state.recordingProcessed ||
          !!state.voiceRecordingPreviewUrl)
      : !!state.message.trim());

  const selectedCategory =
    state.categoryOptions.find((o) => o.value === state.categoryId) || null;

  const hasVoiceFile = !!state.voiceFileUrl;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <PageLayout
        className="templates-page template-create-page"
        backButton={true}
        onBackClick={() => navigate(Path.Templates)}
        backButtonLabel="Back to Templates"
      >
        <div className="people-dashboard-header">
          <span>{isEdit ? "Edit Template" : "New Template"}</span>
        </div>

        <div className="template-create-body">
          {/* ── Left column ───────────────────────────────────────────────── */}
          <div className="template-create-left">
            {/* ── Details section (collapsible) ─────────────────────────── */}
            <div className="template-section">
              <button
                type="button"
                className="template-section-header"
                onClick={() => updateState({ detailsOpen: !state.detailsOpen })}
              >
                <i
                  className={`fa fa-chevron-${state.detailsOpen ? "down" : "right"} template-section-chevron`}
                />
                <span className="template-section-title">Details</span>
              </button>

              {state.detailsOpen && (
                <div className="template-section-body">
                  {/* Communication Method */}
                  <div className="template-create-field-group">
                    <label className="template-create-label">
                      Communication Method
                    </label>
                    <div className="template-comm-method-list">
                      {COMM_METHODS.map((m) => {
                        const active = state.commMethod === m.id;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            className={
                              "template-comm-btn" + (active ? " active" : "")
                            }
                            onClick={() =>
                              updateState({
                                commMethod: m.id,
                                message: "",
                                voiceFileUrl: null,
                                voiceFileIdentifier: null,
                                voiceFileName: null,
                                voiceLocalPreviewUrl: null,
                                voiceFileError: "",
                                voiceMode: "upload",
                                voiceRecordingPreviewUrl: null,
                                isRecording: false,
                                recordedAt: null,
                                showComputerRecorder: null,
                                recordingPhone: "",
                                recordingSessionId: null,
                                recordingCallInProgress: false,
                                recordingStatusPolling: false,
                                recordingProcessed: false,
                                recordingStatusMessage: "",
                                canRetryRecordingCall: false,
                              })
                            }
                          >
                            <span className="template-comm-btn-icon">
                              <i className={`fa ${m.icon}`} />
                            </span>
                            <span className="template-comm-btn-label">
                              {m.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Template Name */}
                  <div className="template-create-field-group">
                    <label className="template-create-label">
                      Template Name
                    </label>
                    <GuiTextbox
                      name="templateName"
                      value={state.templateName}
                      onChange={(_, val) => updateState({ templateName: val })}
                      className="template-name-field"
                      maxLength={100}
                    />
                  </div>

                  {/* Template Category */}
                  <div className="template-create-field-group">
                    <label className="template-create-label">
                      Template Category
                    </label>
                    <div className="template-category-row">
                      <GuiSelect
                        className="template-create-select"
                        name="category"
                        options={state.categoryOptions}
                        value={selectedCategory?.value ?? null}
                        onChange={(_, val) =>
                          updateState({ categoryId: val?.value ?? null })
                        }
                        isSearchable={true}
                        enableClear={true}
                        placeholder="Select"
                      />
                      <GuiButton
                        className="btn-text template-manage-categories-btn"
                        onClick={() =>
                          updateState({ showManageCategories: true })
                        }
                      >
                        <i className="fa fa-plus" />
                        Manage Template Categories
                      </GuiButton>
                    </div>
                  </div>

                  {/* Template ID */}
                  <div className="template-create-field-group">
                    <label className="template-create-label">Template ID</label>
                    <GuiTextbox
                      name="templateId"
                      value={state.templateId || ""}
                      onChange={(_, val) => updateState({ templateId: val })}
                      maxLength={TEMPLATE_ID_MAX}
                      className="template-id-field"
                      error={templateIdError}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="template-section-divider" />

            {/* ── Message section ───────────────────────────────────────── */}
            <div className="template-section">
              <div className="template-section-header no-toggle">
                <i className="fal fa-pencil template-section-icon" />
                <span className="template-section-title">Message</span>
              </div>
              <p className="template-required-note">All fields are required.</p>

              <div className="template-section-body">
                {/* Subject */}
                <div className="template-create-field-group">
                  <GuiTextbox
                    name="subject"
                    label="Subject"
                    value={state.subject}
                    onChange={(_, val) =>
                      updateState({ subject: val, templateName: val })
                    }
                    error={subjectError}
                  />
                </div>

                {/* Voice File section */}
                {isVoice && (
                  <div className="template-create-field-group">
                    <label className="template-create-label">Voice File</label>

                    {/* Hidden file input (upload mode) */}
                    <input
                      ref={voiceFileInputRef}
                      type="file"
                      accept={ALLOWED_VOICE_ACCEPT}
                      style={{ display: "none" }}
                      onChange={handleVoiceFileChange}
                    />

                    {/* Tab toggle: Upload File | Record File */}
                    <div
                      className="template-voice-tab-toggle"
                      data-tab={state.voiceMode}
                    >
                      <button
                        type="button"
                        className={
                          "template-voice-tab" +
                          (state.voiceMode === "upload" ? " active" : "")
                        }
                        onClick={() => {
                          if (
                            state.voiceMode === "record" &&
                            (state.recordingSessionId ||
                              state.recordingStatusPolling ||
                              state.recordingCallInProgress ||
                              state.voiceRecordingPreviewUrl) &&
                            !window.confirm(
                              "Switching to Upload File will clear unsaved recording state. Continue?",
                            )
                          ) {
                            return;
                          }

                          updateState({
                            voiceMode: "upload",
                            voiceFileError: "",
                            recordingPhone: "",
                            recordingSessionId: null,
                            recordingCallInProgress: false,
                            recordingStatusPolling: false,
                            recordingProcessed: false,
                            recordingStatusMessage: "",
                            canRetryRecordingCall: false,
                            voiceRecordingPreviewUrl: null,
                          });
                        }}
                      >
                        Upload File
                      </button>
                      <button
                        type="button"
                        className={
                          "template-voice-tab" +
                          (state.voiceMode === "record" ? " active" : "")
                        }
                        onClick={() => {
                          updateState({
                            voiceMode: "record",
                            voiceFileError: "",
                            showComputerRecorder: null,
                            ...(!isEdit && !state.voiceFileName
                              ? { voiceFileName: generateVoiceFileName() }
                              : {}),
                          });
                        }}
                      >
                        Record File
                      </button>
                    </div>

                    {/* ── Upload mode ───────────────────────────────────── */}
                    {state.voiceMode === "upload" && (
                      <>
                        {!hasVoiceFile ? (
                          <GuiButton
                            className="btn-outline template-voice-btn"
                            onClick={handleVoiceFileClick}
                            loading={state.voiceUploading}
                            disabled={state.voiceUploading}
                          >
                            <i className="fa fa-upload" /> Choose File
                          </GuiButton>
                        ) : (
                          <>
                            <GuiButton
                              className="btn-outline template-voice-btn template-voice-replace-btn"
                              onClick={handleVoiceFileClick}
                              disabled={state.voiceUploading}
                            >
                              <i className="fa fa-upload" /> Replace Voice File
                            </GuiButton>
                            <div className="template-voice-file-row">
                              <i className="fa fa-file-audio template-voice-file-icon" />
                              <span className="template-voice-file-label">
                                Uploaded File:
                              </span>
                              <span className="template-voice-file-name">
                                {state.voiceFileName}
                              </span>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* ── Record mode ───────────────────────────────────── */}
                    {state.voiceMode === "record" && (
                      <div className="template-voice-record-section">
                        {/* Voice File Name — always visible */}
                        <div className="template-voice-record-meta">
                          <GuiTextbox
                            name="voiceFileName"
                            label="Voice File Name"
                            value={state.voiceFileName || ""}
                            onChange={(_, val) =>
                              updateState({ voiceFileName: val })
                            }
                            className="template-name-field"
                            error={voiceFileNameError}
                          />
                        </div>

                        {/* ── After recording: pill row ─────────────────── */}
                        {state.voiceRecordingPreviewUrl ? (
                          <div className="template-voice-recorded-pill">
                            <i className="fa fa-file-audio" />
                            <span>
                              Recorded File:{" "}
                              {state.recordedAt
                                ? state.recordedAt.toLocaleDateString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "2-digit",
                                  }) +
                                  " " +
                                  state.recordedAt.toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""}
                            </span>
                            {state.voiceUploading ? (
                              <i className="fa fa-spinner fa-spin template-voice-pill-spinner" />
                            ) : (
                              <button
                                type="button"
                                className="template-voice-pill-remove"
                                title="Remove recording"
                                onClick={() =>
                                  updateState({
                                    voiceRecordingPreviewUrl: null,
                                    voiceFileUrl: null,
                                    voiceFileIdentifier: null,
                                    recordedAt: null,
                                    voiceFileError: "",
                                    showComputerRecorder: null,
                                    recordingProcessed: false,
                                    recordingSessionId: null,
                                    recordingStatusMessage: "",
                                    canRetryRecordingCall: false,
                                  })
                                }
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        ) : isEdit && state.voiceFileUrl ? (
                          /* ── Edit mode: existing file pill ───────────── */
                          <div className="template-voice-recorded-pill">
                            <i className="fa fa-file-audio" />
                            <span>{state.voiceFileName}</span>
                            <button
                              type="button"
                              className="template-voice-pill-remove"
                              title="Remove file"
                              onClick={() =>
                                updateState({
                                  voiceFileUrl: null,
                                  voiceFileIdentifier: null,
                                  voiceFileError: "",
                                  showComputerRecorder: null,
                                  recordingProcessed: false,
                                  recordingSessionId: null,
                                  recordingStatusMessage: "",
                                  canRetryRecordingCall: false,
                                })
                              }
                            >
                              &times;
                            </button>
                          </div>
                        ) : /* ── No file yet: choose recording method ─────── */
                        state.showComputerRecorder === true ? (
                          /* Computer recording UI */
                          <div className="template-voice-rec-ui">
                            <div className="template-voice-rec-btn-row">
                              <button
                                type="button"
                                className={
                                  "template-voice-rec-circle" +
                                  (state.isRecording ? " recording" : "")
                                }
                                onClick={
                                  state.isRecording
                                    ? stopRecording
                                    : startRecording
                                }
                              >
                                {state.isRecording ? (
                                  <span className="template-voice-rec-stop" />
                                ) : (
                                  <i className="fa fa-microphone" />
                                )}
                              </button>
                              {state.isRecording && (
                                <span className="template-voice-rec-timer">
                                  {String(
                                    Math.floor(recordingSeconds / 60),
                                  ).padStart(2, "0")}
                                  :
                                  {String(recordingSeconds % 60).padStart(
                                    2,
                                    "0",
                                  )}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="template-voice-rec-phone-link"
                              onClick={() =>
                                updateState({ showComputerRecorder: false })
                              }
                            >
                              <i className="fa fa-phone" /> Record on phone call
                              instead
                            </button>
                          </div>
                        ) : state.showComputerRecorder === false ? (
                          /* Phone call recording UI */
                          <div className="template-voice-rec-ui">
                            <p className="template-voice-rec-hint">
                              Type in the phone number you would like to call
                              then click Call
                            </p>
                            <div className="template-voice-rec-call-form">
                              <GuiTextbox
                                name="recordingPhoneInline"
                                label="Phone Number"
                                value={state.recordingPhone || ""}
                                onChange={(_, val) =>
                                  updateState({ recordingPhone: val })
                                }
                                placeholder="Phone Number"
                                className="template-name-field"
                                format="digits"
                                maxLength={10}
                                error={state.voiceFileError}
                              />
                              <GuiButton
                                className="btn-outline template-voice-rec-call-btn"
                                onClick={handleStartPhoneRecording}
                                disabled={
                                  state.recordingCallInProgress ||
                                  state.recordingStatusPolling ||
                                  state.voiceUploading ||
                                  (state.recordingPhone || "").length !== 10
                                }
                              >
                                <i className="fa fa-phone" /> Call
                              </GuiButton>
                            </div>
                            <button
                              type="button"
                              className="template-voice-rec-phone-link"
                              onClick={() =>
                                updateState({ showComputerRecorder: true })
                              }
                              disabled={
                                state.recordingCallInProgress ||
                                state.recordingStatusPolling
                              }
                            >
                              <i className="fa fa-microphone" /> Record on
                              computer instead
                            </button>
                          </div>
                        ) : (
                          /* Initial: two option buttons */
                          <div className="template-voice-rec-options">
                            <button
                              type="button"
                              className="template-voice-rec-option-btn"
                              onClick={() =>
                                updateState({ showComputerRecorder: true })
                              }
                            >
                              <i className="fa fa-microphone" /> Record on
                              computer
                            </button>
                            <span className="template-voice-rec-options-or">
                              or
                            </span>
                            <button
                              type="button"
                              className="template-voice-rec-option-btn"
                              onClick={() =>
                                updateState({ showComputerRecorder: false })
                              }
                            >
                              <i className="fa fa-phone" /> Record on phone call
                            </button>
                          </div>
                        )}

                        {(state.recordingCallInProgress ||
                          state.recordingStatusPolling ||
                          state.recordingStatusMessage) && (
                          <div className="template-voice-call-status">
                            {state.recordingStatusPolling && (
                              <span className="template-voice-processing-lock">
                                Please wait as we process your file...
                                <span className="template-voice-spinner" />
                              </span>
                            )}
                            {!state.recordingStatusPolling &&
                              !!state.recordingStatusMessage && (
                                <span>{state.recordingStatusMessage}</span>
                              )}
                          </div>
                        )}

                        {state.canRetryRecordingCall && (
                          <button
                            type="button"
                            className="template-voice-retry-link"
                            onClick={handleStartPhoneRecording}
                          >
                            I did not get a phone call
                          </button>
                        )}
                      </div>
                    )}

                    {/* Validation / upload errors — hidden when phone UI is active (error shown inline) */}
                    {state.showComputerRecorder !== false &&
                      (state.voiceFileError || voiceFileRequiredError) && (
                        <p className="template-voice-error">
                          {state.voiceFileError || voiceFileRequiredError}
                        </p>
                      )}
                  </div>
                )}

                {/* Email — rich HTML editor */}
                {isEmail && (
                  <div className="template-create-field-group">
                    <GuiHtmlEditor
                      name="message"
                      label="Message"
                      value={state.message}
                      onChange={(_, val) => updateState({ message: val })}
                      fakeRef={emailHtmlEditorRef}
                    />
                  </div>
                )}

                {/* Text / TTS — textarea */}
                {!isVoice && !isEmail && (
                  <div className="template-create-field-group">
                    <GuiTextArea
                      name="message"
                      label={isTTS ? "Text to Speech Message" : "Message"}
                      value={state.message}
                      onChange={(_, val) =>
                        updateState({
                          message: val,
                          ...(isTTS && {
                            ttsConverted: false,
                            ttsAudioUrl: null,
                            ttsDurationSeconds: null,
                            ttsTrim: null,
                            ttsBannerDismissed: false,
                          }),
                        })
                      }
                      maxLength={limit || undefined}
                      className="template-message-textarea"
                      error={messageError}
                    />
                    {limit && (
                      <div
                        className={
                          "template-char-counter" +
                          (atLimit ||
                          (isTTS && charCount >= TTS_CHAR_WARNING_LIMIT)
                            ? " at-limit"
                            : "")
                        }
                      >
                        {charCount}/{limit}
                        {atLimit && " \u2013 Maximum character count reached"}
                        {!atLimit &&
                          isTTS &&
                          charCount >= TTS_CHAR_WARNING_LIMIT &&
                          " \u2013 Approaching the character limit"}
                        {isTTS && (
                          <span className="template-tts-estimated-duration">
                            {" "}
                            &middot; Est. length:{" "}
                            {formatDurationShort(
                              estimateTtsDurationSecondsCalibrated(
                                charCount,
                                state.ttsCharsPerSecond,
                              ),
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    {isTTS && (
                      <GuiButton
                        className="btn-secondary template-convert-speech-btn"
                        disabled={!state.message.trim() || state.ttsConverted}
                        loading={state.convertingTTS}
                        onClick={handleConvertToSpeech}
                      >
                        Convert to Speech
                      </GuiButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Right column: Live preview + action buttons ───────────────── */}
          <div className="template-create-right" ref={rightPanelRef}>
            {/* Drag handle — full-height strip on the left edge */}
            <div
              className="template-resize-handle"
              onMouseDown={onDragHandleMouseDown}
              title="Drag to resize preview"
            >
              <span className="template-resize-grip" />
            </div>
            {/* Live preview panel */}
            <div
              className={
                "template-preview-section" + (isVoice || isTTS ? " voice" : "")
              }
            >
              <div className="template-preview-header">
                <i className="fa fa-eye template-preview-icon" />
                <span className="template-section-title">Preview</span>
              </div>

              <div className="template-preview-body">
                {isVoice ? (
                  <div className="template-voice-preview">
                    {state.subject && (
                      <p className="template-voice-preview-subject">
                        {state.subject}
                      </p>
                    )}
                    {(() => {
                      // voiceRecordingPreviewUrl = public blob URL after phone recording completes.
                      // voiceLocalPreviewUrl     = data: URL served by GetTemplateById in edit mode.
                      const playableUrl =
                        state.voiceRecordingPreviewUrl ||
                        state.voiceLocalPreviewUrl;
                      const hasFile =
                        playableUrl ||
                        state.voiceFileIdentifier ||
                        state.voiceFileUrl;

                      if (playableUrl) {
                        return (
                          <>
                            {state.voiceFileName && (
                              <p className="template-voice-preview-filename">
                                {state.voiceFileName}
                              </p>
                            )}
                            <AudioPlayer
                              key={playableUrl}
                              src={playableUrl}
                              styled
                            />
                          </>
                        );
                      }
                      if (state.loading) {
                        return (
                          <span className="template-preview-placeholder">
                            Loading preview&hellip;
                          </span>
                        );
                      }
                      if (hasFile) {
                        return (
                          <span className="template-preview-placeholder">
                            Voice file attached — audio preview unavailable
                          </span>
                        );
                      }
                      return (
                        <span className="template-preview-placeholder">
                          Your voice file will appear here&hellip;
                        </span>
                      );
                    })()}
                  </div>
                ) : isEmail ? (
                  <div className="template-email-preview">
                    <div className="template-email-preview-subject">
                      {state.subject || "Subject"}
                    </div>
                    <div className="template-email-preview-sender">
                      <div className="template-email-preview-avatar">
                        <i className="fa fa-user" />
                      </div>
                      <div className="template-email-preview-sender-info">
                        <span className="template-email-preview-sender-name">
                          Sender Email
                        </span>
                        <span className="template-email-preview-recipient">
                          to: [recipient list]
                        </span>
                      </div>
                    </div>
                    <div
                      className="template-email-preview-body"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(state.message),
                      }}
                    />
                  </div>
                ) : isTTS ? (
                  <div className="template-tts-preview">
                    {state.convertingTTS ? (
                      <p className="template-tts-converting">
                        Converting to speech&hellip;
                      </p>
                    ) : state.ttsAudioUrl ? (
                      <>
                        <p className="template-voice-preview-filename">
                          {state.subject ? `${state.subject}.mp3` : "audio.mp3"}
                        </p>
                        <AudioPlayer
                          key={state.ttsAudioUrl}
                          src={state.ttsAudioUrl}
                          styled
                        />
                        {state.ttsDurationSeconds > 0 && (
                          <div className="tts-measured-duration">
                            Converted length:{" "}
                            {formatDurationShort(state.ttsDurationSeconds)}
                          </div>
                        )}
                        {state.ttsTrim?.overflowText && (
                          <div className="tts-trim-preview">
                            <span className="tts-trim-kept">
                              {state.ttsTrim.keptText}
                            </span>{" "}
                            <span
                              className="tts-trim-overflow"
                              title="Not included in the voice recording — exceeds the 2 minute limit"
                            >
                              {state.ttsTrim.overflowText}
                            </span>
                          </div>
                        )}
                        {state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS &&
                          !state.ttsBannerDismissed && (
                            <div className="tts-duration-banner">
                              <div className="tts-duration-banner-message">
                                This recording is{" "}
                                {formatDurationShort(state.ttsDurationSeconds)}{" "}
                                —{" "}
                                {Math.max(
                                  0,
                                  Math.round(
                                    state.ttsDurationSeconds -
                                      MAX_TTS_DURATION_SECONDS,
                                  ),
                                )}{" "}
                                seconds over the 2 minute limit.
                              </div>
                              <div className="tts-duration-banner-actions">
                                <GuiButton
                                  className="tts-shorten-to-fit"
                                  disabled={state.convertingTTS}
                                  onClick={handleShortenToFit}
                                >
                                  Shorten to fit
                                </GuiButton>
                                <GuiButton
                                  className="btn-outline"
                                  disabled={state.convertingTTS}
                                  onClick={handleTrimTo105}
                                >
                                  Trim to 1:45
                                </GuiButton>
                                <GuiButton
                                  className="btn-outline"
                                  onClick={dismissTtsDurationBanner}
                                >
                                  Edit message
                                </GuiButton>
                              </div>
                            </div>
                          )}
                      </>
                    ) : (
                      <span className="template-preview-placeholder">
                        Click &ldquo;Convert to Speech&rdquo; to preview
                        audio&hellip;
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="template-preview-text">
                    {state.message ? (
                      state.message
                    ) : (
                      <span className="template-preview-placeholder">
                        Your message will appear here&hellip;
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Voice preview call section — inside preview section for voice mode */}
              {isVoice && (
                <div className="template-preview-call-section">
                  <p className="template-preview-call-hint">
                    Enter a phone number below to receive a preview of your
                    message
                  </p>
                  <div className="template-preview-call-row">
                    <input
                      type="tel"
                      className="template-preview-call-input"
                      placeholder="Phone Number"
                      value={state.previewPhone || ""}
                      onChange={(e) =>
                        updateState({
                          previewPhone: onlyDigits(e.target.value).slice(0, 10),
                        })
                      }
                    />
                    <input
                      type="text"
                      className="template-preview-call-input template-preview-call-ext"
                      placeholder="Extension"
                      value={state.previewExtension || ""}
                      onChange={(e) =>
                        updateState({
                          previewExtension: onlyDigits(e.target.value).slice(
                            0,
                            6,
                          ),
                        })
                      }
                    />
                    <GuiButton
                      className={
                        "btn-outline template-preview-call-btn" +
                        (!state.voiceFileUrl && !state.voiceRecordingPreviewUrl
                          ? " disabled"
                          : "")
                      }
                      onClick={handleSendPreviewCall}
                      disabled={
                        !state.voiceFileUrl && !state.voiceRecordingPreviewUrl
                      }
                    >
                      Call
                    </GuiButton>
                  </div>
                </div>
              )}

              {/* TTS preview call section */}
              {isTTS && (
                <div className="template-preview-call-section">
                  <p className="template-preview-call-hint">
                    Enter a phone number below to receive a preview of your
                    message
                  </p>
                  <div className="template-preview-call-row">
                    <input
                      type="tel"
                      className="template-preview-call-input"
                      placeholder="Phone Number"
                      value={state.ttsPreviewPhone || ""}
                      onChange={(e) =>
                        updateState({
                          ttsPreviewPhone: onlyDigits(e.target.value).slice(
                            0,
                            10,
                          ),
                        })
                      }
                    />
                    <input
                      type="text"
                      className="template-preview-call-input template-preview-call-ext"
                      placeholder="Extension"
                      value={state.ttsPreviewExtension || ""}
                      onChange={(e) =>
                        updateState({
                          ttsPreviewExtension: onlyDigits(e.target.value).slice(
                            0,
                            6,
                          ),
                        })
                      }
                    />
                    <GuiButton
                      className="btn-outline template-preview-call-btn"
                      onClick={handleSendTtsPreviewCall}
                      disabled={!state.ttsAudioUrl}
                    >
                      Call
                    </GuiButton>
                  </div>
                </div>
              )}
            </div>

            {/* Cancel / Save */}
            <div className="template-create-actions">
              <GuiButton
                className="btn-outline"
                onClick={() => navigate(Path.Templates)}
              >
                Cancel
              </GuiButton>
              <GuiButton
                className="btn-primary"
                loading={state.saving}
                onClick={save}
                disabled={!canSave}
              >
                <i className="fa fa-save" /> {isEdit ? "Update" : "Save"}
              </GuiButton>
            </div>
          </div>
        </div>
      </PageLayout>

      {state.showManageCategories && (
        <ManageTemplateCategoriesModal
          onClose={(categoriesChanged) => {
            updateState({ showManageCategories: false });
            if (categoriesChanged) loadCategoryOptions();
          }}
        />
      )}
    </>
  );
}
